import { ButtonVariant } from '@wyz/ui';
import { FEATURES, ROLES } from '~/common/helpers';
import {
  FeatureFlagEnum,
  IFeatureFlags,
  TranslationKeys,
} from '~/common/types';

export type TileAction = {
  labelKey: keyof TranslationKeys;
  to: string;
  variant: ButtonVariant;
};

export type TileConfig = {
  trackingId: string;
  titleKey: keyof TranslationKeys;
  descriptionKey: keyof TranslationKeys;
  action: TileAction;
  column: {
    xs: number; // mobile
    sm: number; // mobile landscape
    md: number; // tablet
    lg: number; // desktop
  };
  image?: string;
  bgVariant: 'white' | 'neutral-grey-1';
};

export type TileVariant = {
  roles: string[];
  features: FeatureFlagEnum[];
  tiles: TileConfig[];
};

const PURCHASE_TIRES: TileConfig = {
  trackingId: 'purchase_tires',
  titleKey: 'app.home.tiles.purchase_tires.title',
  descriptionKey: 'app.home.tiles.purchase_tires.description',
  action: {
    labelKey: 'app.home.tiles.purchase_tires.action',
    to: '/purchase',
    variant: 'primary',
  },
  column: { lg: 6, md: 12, sm: 12, xs: 12 },
  image: '/img/tiles/Purchase_Tires.webp',
  bgVariant: 'neutral-grey-1',
};

const ORDER_HISTORY: TileConfig = {
  trackingId: 'order_history',
  titleKey: 'app.home.tiles.order_history.title',
  descriptionKey: 'app.home.tiles.order_history.description',
  action: {
    labelKey: 'app.home.tiles.order_history.action',
    to: '/purchase/orders',
    variant: 'primary',
  },
  column: { lg: 6, md: 12, sm: 12, xs: 12 },
  image: '/img/tiles/Order_History.webp',
  bgVariant: 'white',
};
const CREATE_QUOTE: TileConfig = {
  trackingId: 'create_quote',
  titleKey: 'app.home.tiles.create_quote.title',
  descriptionKey: 'app.home.tiles.create_quote.description',
  action: {
    labelKey: 'app.home.tiles.create_quote.action',
    to: '/quotes/search',
    variant: 'primary',
  },
  column: { lg: 6, md: 12, sm: 12, xs: 12 },
  image: '/img/tiles/Create_Quote.webp',
  bgVariant: 'neutral-grey-1',
};

const QUOTE_HISTORY: TileConfig = {
  trackingId: 'quote_history',
  titleKey: 'app.home.tiles.quote_history.title',
  descriptionKey: 'app.home.tiles.quote_history.description',
  action: {
    labelKey: 'app.home.tiles.quote_history.action',
    to: '/quotes/history',
    variant: 'primary',
  },
  column: { lg: 6, md: 12, sm: 12, xs: 12 },
  image: '/img/tiles/Quote_History.webp',
  bgVariant: 'white',
};

const DIAGNOSTIC_OVERVIEW: TileConfig = {
  trackingId: 'diagnostic_overview',
  titleKey: 'app.home.tiles.diagnostic_overview.title',
  descriptionKey: 'app.home.tiles.diagnostic_overview.description',
  action: {
    labelKey: 'app.home.tiles.diagnostic_overview.action',
    to: '/quotes/diagnosis',
    variant: 'primary',
  },
  column: { lg: 6, md: 12, sm: 12, xs: 12 },
  image: '/img/tiles/Diagnostic_Overview.webp',
  bgVariant: 'white',
};

const featureFlagLookup: Record<FeatureFlagEnum, keyof IFeatureFlags> = {
  [FEATURES.QUOTE]: 'enabledQuote',
  [FEATURES.REDISTRIBUTION]: 'enabledRedistribution',
  [FEATURES.QUOTE_EXPRESS]: 'enabledQuoteExpress',
  [FEATURES.CHAT]: 'enabledChat',
  [FEATURES.CONTAINER]: 'enabledContainer',
  [FEATURES.EXTRA_BONUS]: 'enabledExtraBonus',
  [FEATURES.DESTOCKING]: 'enabledDestocking',
};

type TileRequirement = {
  tile: TileConfig;
  roles: string[];
  features: FeatureFlagEnum[];
};

const TILE_REQUIREMENTS: TileRequirement[] = [
  {
    tile: PURCHASE_TIRES,
    roles: [ROLES.ROLE_LIST_OFFERS],
    features: [],
  },
  {
    tile: CREATE_QUOTE,
    roles: [ROLES.ROLE_QUOTE_CREATE],
    features: [FEATURES.QUOTE],
  },
  {
    tile: ORDER_HISTORY,
    roles: [ROLES.ROLE_LIST_ORDERS],
    features: [],
  },
  {
    tile: QUOTE_HISTORY,
    roles: [ROLES.ROLE_QUOTE_LIST],
    features: [FEATURES.QUOTE],
  },
  {
    tile: DIAGNOSTIC_OVERVIEW,
    roles: [ROLES.ROLE_DIAGNOSIS_LIST],
    features: [FEATURES.QUOTE_EXPRESS],
  },
];

export function resolveVariant(
  userRoles: string[],
  enabledFeatures: Partial<IFeatureFlags>,
): TileConfig[] {
  const tiles = TILE_REQUIREMENTS.filter(
    (req) =>
      req.roles.every((r) => userRoles.includes(r)) &&
      req.features.every((f) => enabledFeatures[featureFlagLookup[f]] === true),
  ).map((req) => ({
    ...req.tile,
    column: { ...req.tile.column, lg: 6, md: 12 },
  }));

  // if there's an odd number of tiles, make the last one take the full width
  if (tiles.length % 2 !== 0 && tiles.length > 0) {
    tiles[tiles.length - 1] = {
      ...tiles[tiles.length - 1],
      column: { ...tiles[tiles.length - 1].column, lg: 12 },
    };
  }

  return tiles;
}
